import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { authedJson } from '@/lib/http';

interface OverviewTotals {
  orderCount: number;
  grossAmountCents: string | null;
  netCollectedCents: string | null;
  processingFeeCents: string | null;
  platformFeeCents: string | null;
  creatorEarningsCents: string | null;
  refundTotalCents: string | null;
  chargebackTotalCents: string | null;
}

interface TypeRow {
  orderType: string;
  orderCount: number;
  grossAmountCents: string | null;
  netCollectedCents: string | null;
  platformFeeCents: string | null;
  creatorEarningsCents: string | null;
}

interface OverviewResp {
  totals: OverviewTotals;
  byType: TypeRow[];
}

interface Bucket {
  bucket: string;
  orderCount: number;
  grossAmountCents: string | null;
  netCollectedCents: string | null;
  platformFeeCents: string | null;
  creatorEarningsCents: string | null;
  processingFeeCents: string | null;
  refundTotalCents: string | null;
}

interface TimeseriesResp {
  interval: string;
  buckets: Bucket[];
}

const n = (v: string | number | null | undefined): number => Number(v ?? 0);

const usd = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const usdShort = (cents: number): string => {
  const d = cents / 100;
  if (Math.abs(d) >= 1000) return `$${(d / 1000).toFixed(1)}k`;
  return `$${d.toFixed(0)}`;
};

const RANGES = [
  { value: '7d', label: 'Last 7 days', days: 7, interval: 'day' },
  { value: '30d', label: 'Last 30 days', days: 30, interval: 'day' },
  { value: '90d', label: 'Last 90 days', days: 90, interval: 'day' },
  { value: '365d', label: 'Last 12 months', days: 365, interval: 'week' },
  { value: 'all', label: 'All time', days: null, interval: 'month' },
] as const;

const TYPE_LABELS: Record<string, string> = {
  class: 'Class bookings',
  pass: 'Punch passes',
  tip: 'Tips',
};

function StatCard({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: string;
  hint?: string;
  emphasis?: boolean;
}) {
  return (
    <Card className={emphasis ? 'border-primary/40 bg-primary/5' : undefined}>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

export function FinancialsOverview() {
  const [range, setRange] = useState<(typeof RANGES)[number]['value']>('30d');
  const cfg = RANGES.find(r => r.value === range) ?? RANGES[1];

  const sinceIso = useMemo(() => {
    if (cfg.days == null) return null;
    const d = new Date();
    d.setDate(d.getDate() - cfg.days);
    return d.toISOString();
  }, [cfg.days]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (sinceIso) p.set('since', sinceIso);
    return p.toString();
  }, [sinceIso]);

  const { data: overview, isLoading: overviewLoading } = useQuery<OverviewResp>({
    queryKey: ['/api/admin/financials/overview', range],
    queryFn: () => authedJson<OverviewResp>(`/api/admin/financials/overview?${qs}`),
  });

  const { data: series, isLoading: seriesLoading } = useQuery<TimeseriesResp>({
    queryKey: ['/api/admin/financials/timeseries', range],
    queryFn: () =>
      authedJson<TimeseriesResp>(
        `/api/admin/financials/timeseries?${qs}${qs ? '&' : ''}interval=${cfg.interval}`
      ),
  });

  const totals = overview?.totals;
  const gross = n(totals?.grossAmountCents);
  const net = n(totals?.netCollectedCents);
  const platform = n(totals?.platformFeeCents);
  const creator = n(totals?.creatorEarningsCents);
  const processing = n(totals?.processingFeeCents);
  const refunds = n(totals?.refundTotalCents);

  const chartData = useMemo(
    () =>
      (series?.buckets ?? []).map(b => ({
        date: new Date(b.bucket).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        sales: n(b.netCollectedCents) / 100,
        platform: n(b.platformFeeCents) / 100,
      })),
    [series]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Financial overview</h2>
          <p className="text-sm text-muted-foreground">
            Sales collected and platform income after Stripe fees and creator payouts.
          </p>
        </div>
        <Select value={range} onValueChange={v => setRange(v as typeof range)}>
          <SelectTrigger className="w-[170px]" data-testid="select-fin-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map(r => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard
          label="Total sales"
          value={usd(net)}
          hint={gross !== net ? `${usd(gross)} before discounts` : `${n(totals?.orderCount)} orders`}
          emphasis
        />
        <StatCard label="Platform income" value={usd(platform)} hint="after fees & payouts" emphasis />
        <StatCard label="Creator payouts" value={usd(creator)} />
        <StatCard label="Stripe fees" value={usd(processing)} hint="absorbed by Kalovate" />
        <StatCard label="Refunds" value={usd(refunds)} />
        <StatCard label="Orders" value={String(n(totals?.orderCount))} />
      </div>

      {/* Over-time chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Payments over time</CardTitle>
        </CardHeader>
        <CardContent>
          {seriesLoading ? (
            <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
              Loading…
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
              No payments in this period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="fillSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="fillPlatform" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(142 71% 45%)" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="hsl(142 71% 45%)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
                <XAxis dataKey="date" tickLine={false} axisLine={false} fontSize={11} minTickGap={24} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  fontSize={11}
                  width={52}
                  tickFormatter={(v: number) => usdShort(v * 100)}
                />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    usd(Number(value) * 100),
                    name === 'sales' ? 'Total sales' : 'Platform income',
                  ]}
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid hsl(var(--border))',
                    background: 'hsl(var(--background))',
                    fontSize: 12,
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke="hsl(var(--primary))"
                  fill="url(#fillSales)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="platform"
                  stroke="hsl(142 71% 45%)"
                  fill="url(#fillPlatform)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Breakdown by type */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sales by type</CardTitle>
        </CardHeader>
        <CardContent>
          {overviewLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !overview?.byType?.length ? (
            <p className="text-sm text-muted-foreground">No sales in this period.</p>
          ) : (
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="py-2 pr-4 font-medium">Type</th>
                    <th className="py-2 pr-4 text-right font-medium">Orders</th>
                    <th className="py-2 pr-4 text-right font-medium">Sales</th>
                    <th className="py-2 pr-4 text-right font-medium">Platform income</th>
                    <th className="py-2 text-right font-medium">Creator payouts</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.byType.map(t => (
                    <tr key={t.orderType} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">
                        {TYPE_LABELS[t.orderType] ?? t.orderType}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{t.orderCount}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {usd(n(t.netCollectedCents))}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {usd(n(t.platformFeeCents))}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {usd(n(t.creatorEarningsCents))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
